import * as React from "react"
import { useTheme } from "@chakra-ui/react"
import { MapLayerMouseEvent } from "react-map-gl"
import Mapbox from "../mapbox/Mapbox"
import GeoJSONLayer from "../mapbox/GeoJSONLayer"
import { SIDEBAR_WIDTH } from "../lib/config"
import { Bbox, Coord, getZonesGeojson } from "../lib/helpers/map"
import { Route, TripEntity } from "../lib/types/trip"
import { ZoneEntity } from "../lib/types/zone"
import TripsMapKey from "./TripsMapKey"
import TripLayerMapbox from "./TripLayerMapbox"
import PolylineLayer, { POLYLINE_LAYER_SUFFIX } from "../mapbox/PolylineLayer"
import type GeoJSON from "geojson"
import { useFillOpacity } from "../lib/hooks/useDropZoneStyle"
import { useTripsColorObj } from "../lib/hooks/useTripsStyle"
import EmptyLayer from "../mapbox/EmptyLayer"

export interface TripsMapProps {
  bbox: Bbox
  trips: TripEntity[]
  selectedTrip?: TripEntity
  onSelectTrip: (trip: TripEntity) => void
  showServiceZones: boolean
  serviceZones: ZoneEntity[] | undefined
  showDropzones: boolean
  dropzones: ZoneEntity[] | undefined
  showMaintenanceZones: boolean
  maintenanceZones: ZoneEntity[] | undefined
}

export const TripsMapMapbox: React.FC<TripsMapProps> = ({
  bbox,
  trips,
  selectedTrip,
  onSelectTrip,
  showServiceZones,
  serviceZones,
  showDropzones,
  dropzones,
  showMaintenanceZones,
  maintenanceZones,
}) => {
  const theme = useTheme()
  const [hoveredTrip, setHoveredTrip] = React.useState<TripEntity | undefined>()
  const polygonsFillOpacity = useFillOpacity()
  const tripsColor = useTripsColorObj()

  const gaps = React.useMemo(
    () =>
      trips
        .filter((t) => !!t.route)
        .reduce((acc, curr, index) => {
          const nextIndex = index + 1
          const hasNext = nextIndex !== trips.length
          if (hasNext && !!trips[nextIndex].route) {
            const lastOne = trips[nextIndex].route!.coordinates.length - 1
            const firstNode = [curr.route!.coordinates[0][0], curr.route!.coordinates[0][1]] as Coord
            const lastNode = [
              trips[nextIndex].route!.coordinates[lastOne][0],
              trips[nextIndex].route!.coordinates[lastOne][1],
            ] as Coord
            return [[firstNode, lastNode], ...acc]
          }
          return acc
        }, []),
    [trips],
  )

  // get an array of IDs that are also used to create geoJSON layers
  // in order to pass them to the map and make them interactive
  const layersIds = React.useMemo(
    () => trips.map((trip) => `trip_${trip.uuid}${POLYLINE_LAYER_SUFFIX}`),
    [trips],
  )

  const onClick = (event: MapLayerMouseEvent) => {
    const { features } = event
    const clickedFeature = features && features[0]
    if (!clickedFeature) return
    const clickedTrip = clickedFeature.properties
    if (!clickedTrip) return
    const parsedRoute = JSON.parse(clickedTrip.route) as Route
    const trip = { ...clickedTrip, route: parsedRoute } as TripEntity
    onSelectTrip(trip)
  }

  const onMouseOver = (event: MapLayerMouseEvent) => {
    const { features } = event
    const hoveredFeature = features && features[0]
    if (!hoveredFeature) {
      setHoveredTrip(undefined)
      return
    }
    const hoveredTrip = hoveredFeature.properties as TripEntity
    setHoveredTrip(hoveredTrip)
  }

  return (
    <Mapbox
      fitBounds={bbox}
      fitBoundsOptions={{
        duration: 50,
        linear: true,
        padding: { left: SIDEBAR_WIDTH, top: 50, right: 50, bottom: 50 },
      }}
      style={{
        height: "100%",
        width: "100%",
      }}
      interactiveLayerIds={layersIds}
      onClick={onClick}
      onMouseMove={onMouseOver}
    >
      {/* ---------- ZONES ----------- */}
      {!!showServiceZones && !!serviceZones && (
        <GeoJSONLayer
          id="trips_map_service"
          beforeId="trips_map_zones_trips_boundary"
          data={getZonesGeojson(serviceZones) as GeoJSON.FeatureCollection}
          linePaint={{
            lineColor: theme.colors.purple["800"],
          }}
          fillPaint={{
            fillColor: theme.colors.purple["500"],
            fillOpacity: polygonsFillOpacity,
          }}
        />
      )}

      {!!showDropzones && !!dropzones && (
        <GeoJSONLayer
          id="trips_map_dropzones"
          beforeId="trips_map_zones_trips_boundary"
          data={getZonesGeojson(dropzones) as GeoJSON.FeatureCollection}
          linePaint={{
            lineColor: theme.colors.blue["800"],
          }}
          fillPaint={{
            fillColor: theme.colors.blue["500"],
            fillOpacity: polygonsFillOpacity,
          }}
        />
      )}

      {!!showMaintenanceZones && !!maintenanceZones && (
        <GeoJSONLayer
          id="trips_map_maintenance"
          beforeId="trips_map_zones_trips_boundary"
          data={getZonesGeojson(maintenanceZones) as GeoJSON.FeatureCollection}
          linePaint={{
            lineColor: theme.colors.gray["800"],
          }}
          fillPaint={{
            fillColor: theme.colors.gray["500"],
            fillOpacity: polygonsFillOpacity,
          }}
        />
      )}

      <EmptyLayer id="trips_map_zones_trips_boundary" />

      {/* GAPS */}
      {gaps.map((gap, idx) => (
        <PolylineLayer
          key={`gap_${idx}`}
          id={`gap_${idx}`}
          points={gap}
          paint={{
            lineColor: tripsColor["700"],
            lineOpacity: 0.7,
            lineWidth: 5,
            lineDasharray: [1, 2],
          }}
          layout={{
            lineJoin: "round",
            lineCap: "round",
          }}
        />
      ))}

      {/* TRIPS */}
      {trips.map((trip) => (
        <TripLayerMapbox
          key={trip.uuid}
          trip={trip}
          isHovered={hoveredTrip?.uuid === trip.uuid || false}
          isSelected={selectedTrip?.uuid === trip.uuid || false}
        />
      ))}

      <TripsMapKey />
    </Mapbox>
  )
}
